/**
 * Driver / customer details panel — extracted from `BookingWidget.jsx`.
 * Required identity fields flow into both the booking record AND the customer's
 * saved profile so they don't have to retype on the next rental. Also surfaces
 * a Second-driver sub-form when the renter has picked the matching add-on.
 */
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import CountryCombobox from "@/components/profile/CountryCombobox";

// Field definitions kept declarative so localisation and validation stay
// adjacent. `key` matches both the form value key and the testid suffix.
const PRIMARY_DRIVER_FIELDS = (t) => [
  { key: "full_name", label: t("bookingWidget.fullName"), type: "text" },
  { key: "email", label: t("bookingWidget.email"), type: "email" },
  { key: "phone", label: t("bookingWidget.phone"), type: "text" },
  { key: "id_number", label: t("bookingWidget.idNumber"), type: "text" },
  { key: "license_number", label: t("bookingWidget.licenseNumber"), type: "text" },
  { key: "license_expiry", label: t("bookingWidget.licenseExpiry"), type: "date" },
];

const SECOND_DRIVER_FIELDS = (t) => [
  { key: "full_name", label: t("bookingWidget.fullName"), type: "text" },
  { key: "license_number", label: t("bookingWidget.secondDriverLicense"), type: "text" },
  { key: "license_expiry", label: t("bookingWidget.secondDriverExpiry"), type: "date" },
];

function _hasSecondDriverAddon(car, addOnIds) {
  return (car.add_ons || []).some(
    (a) => addOnIds.includes(a.id) && (a.name || "").toLowerCase().startsWith("second driver"),
  );
}

export function BookingDriverDetails({
  car,
  customerDetails,
  setCustomerDetails,
  secondDriver,
  setSecondDriver,
  addOnIds = [],
}) {
  const { t } = useTranslation();
  if (!setCustomerDetails) return null;

  const showSecondDriver = setSecondDriver && _hasSecondDriverAddon(car, addOnIds);

  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50/30 px-3 py-3 mb-3" data-testid="checkout-driver-details">
      <div className="text-xs font-semibold uppercase tracking-wider text-blue-900 mb-0.5">
        {t("bookingWidget.driverDetails")} <span className="text-rose-500">*</span>
      </div>
      <p className="text-[11px] text-neutral-500 mb-3">{t("bookingWidget.driverDetailsHelp")}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
        {PRIMARY_DRIVER_FIELDS(t).map(({ key, label, type }) => (
          <div key={key} className="flex flex-col gap-0.5">
            <Label htmlFor={`checkout-driver-${key}`} className="text-[10px] uppercase tracking-wider text-neutral-600">
              {label}<span className="text-rose-500 ml-0.5">*</span>
            </Label>
            <input
              id={`checkout-driver-${key}`}
              type={type}
              value={customerDetails[key] || ""}
              onChange={(e) => setCustomerDetails({ ...customerDetails, [key]: e.target.value })}
              placeholder={label}
              required
              className="rounded-md border border-input bg-white px-2 py-1.5 placeholder:text-neutral-400"
              data-testid={`checkout-driver-${key}`}
            />
          </div>
        ))}
        {Number(car?.minimum_driver_age) > 0 && (
          <div className="flex flex-col gap-0.5">
            <Label htmlFor="checkout-driver-date_of_birth" className="text-[10px] uppercase tracking-wider text-neutral-600">
              Date of birth<span className="text-rose-500 ml-0.5">*</span>
            </Label>
            <input
              id="checkout-driver-date_of_birth"
              type="date"
              value={customerDetails.date_of_birth || ""}
              onChange={(e) => setCustomerDetails({ ...customerDetails, date_of_birth: e.target.value })}
              required
              className="rounded-md border border-input bg-white px-2 py-1.5 placeholder:text-neutral-400"
              data-testid="checkout-driver-date_of_birth"
            />
            <span className="text-[10px] text-neutral-500">Required — minimum age is {car.minimum_driver_age}.</span>
          </div>
        )}
        <div className="sm:col-span-2 flex flex-col gap-0.5">
          <Label htmlFor="checkout-driver-address" className="text-[10px] uppercase tracking-wider text-neutral-600">
            {t("bookingWidget.address")}<span className="text-rose-500 ml-0.5">*</span>
          </Label>
          <input
            id="checkout-driver-address"
            type="text"
            value={customerDetails.address || ""}
            onChange={(e) => setCustomerDetails({ ...customerDetails, address: e.target.value })}
            placeholder={t("bookingWidget.addressPlaceholder")}
            required
            className="rounded-md border border-input bg-white px-2 py-1.5 placeholder:text-neutral-400"
            data-testid="checkout-driver-address"
          />
        </div>
        <div className="sm:col-span-2 flex flex-col gap-0.5">
          <Label className="text-[10px] uppercase tracking-wider text-neutral-600">
            {t("bookingWidget.country")}<span className="text-rose-500 ml-0.5">*</span>
          </Label>
          <CountryCombobox
            value={customerDetails.country || ""}
            onChange={(code) => setCustomerDetails({ ...customerDetails, country: code })}
            placeholder={t("bookingWidget.countryPlaceholder")}
            testId="checkout-driver-country"
          />
        </div>
      </div>

      {showSecondDriver && (
        <div className="mt-3 pt-3 border-t border-blue-100">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-600 mb-2">
            {t("bookingWidget.secondDriver")}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
            {SECOND_DRIVER_FIELDS(t).map(({ key, label, type }) => (
              <input
                key={key}
                type={type}
                value={secondDriver[key] || ""}
                onChange={(e) => setSecondDriver({ ...secondDriver, [key]: e.target.value })}
                placeholder={label}
                className="rounded-md border border-input bg-white px-2 py-1.5 placeholder:text-neutral-400"
                data-testid={`checkout-sd-${key}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default BookingDriverDetails;
